import { Fragment, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import {
  DeploymentRuleConditionExpandedSchema,
  DeploymentRuleConditionSchema,
  DeploymentRuleOperator,
  EntityRefUtil,
  FormType,
  GetDeploymentRuleByIdResponseDTO,
  RecordState,
  TagEntity,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';
import { GearbeltAssetType } from '@site-mate/sitemate-global-shared/lib/models/billings/gearbelt-asset-type.enum';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { match } from 'ts-pattern';
import { z } from 'zod';

import { deploymentRuleKeys } from '@/queries/deployments';
import { ApiService } from '@/services/api-v2';
import { getWorkspaceRoute } from '@/utils/routes';

import { DeleteConfirmationDialog } from '../common/delete-confirmation-dialog';
import { SaveChangesDialog } from '../common/save-changes-dialog';
import ClockIcon from '../icons/clock';
import CloseIcon from '../icons/close';
import DeleteIcon from '../icons/delete';
import DeploymentIcon from '../icons/deployment';
import EllipsisHorizontalIcon from '../icons/ellipsis-horizontal';
import FormChecklistIcon from '../icons/form-checklist';
import PlusIcon from '../icons/plus';
import QuestionIcon from '../icons/question';
import WarningTriangleIcon from '../icons/warning-triangle';
import { FormTopbar } from '../topbar/form-topbar';
import { useFormTopbarStore } from '../topbar/form-topbar-store';
import { AutoResizeInput } from '../ui/auto-resize-input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { AsyncCreatableMultiSelect, AsyncMultiSelect } from '../ui/multi-select';
import { Select } from '../ui/select';
import { useToast } from '../ui/toaster';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const deploymentFormSchema = z
  .object({
    name: z.string(),
    conditions: z.array(
      DeploymentRuleConditionExpandedSchema.superRefine((condition, ctx) => {
        if (Array.isArray(condition.value) && condition.value.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Value is required',
            path: ['value'],
          });
        } else if (!Array.isArray(condition.value) && condition.value.value === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Value is required',
            path: ['value'],
          });
        }
      }),
    ),
    inspectionFormIds: z.array(z.string()),
    repairFormIds: z.array(z.string()),
    scheduleIds: z.array(z.string()),
  })
  .refine((data) => data.inspectionFormIds.length > 0 || data.repairFormIds.length > 0 || data.scheduleIds.length > 0, {
    message: 'At least one form or schedule is required',
    path: ['inspectionFormIds'],
  });

const ATTRIBUTE_OPTIONS: { label: string; value: z.infer<typeof DeploymentRuleConditionSchema>['attribute'] }[] = [
  {
    label: 'Asset ID',
    value: 'assetId',
  },
  {
    label: 'Asset Type',
    value: 'assetType',
  },
  {
    label: 'Asset Tag',
    value: 'assetTagId',
  },
  {
    label: 'Asset Category',
    value: 'assetCategoryId',
  },
  {
    label: 'Asset Manufacturer',
    value: 'assetManufacturer',
  },
  {
    label: 'Asset Model',
    value: 'assetModel',
  },
  {
    label: 'Asset Location',
    value: 'assetLocationId',
  },
];

const OPERATOR_OPTIONS = [
  { label: 'Is', value: DeploymentRuleOperator.In },
  { label: 'Is not', value: DeploymentRuleOperator.Nin },
] as { label: string; value: z.infer<typeof DeploymentRuleConditionSchema>['operator'] }[];

const ATTRIBUTE_FORM_INPUT_TYPE_MAP: Record<z.infer<typeof DeploymentRuleConditionSchema>['attribute'], 'multi-select' | 'text' | 'creatable-multi-select'> = {
  assetId: 'multi-select',
  assetType: 'multi-select',
  assetTagId: 'multi-select',
  assetCategoryId: 'multi-select',
  assetManufacturer: 'creatable-multi-select',
  assetModel: 'creatable-multi-select',
  assetLocationId: 'multi-select',
};

export function DeploymentForm({ deploymentRule, isLoading }: { deploymentRule?: GetDeploymentRuleByIdResponseDTO['deploymentRule']; isLoading: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [openSaveChanges, setOpenSaveChanges] = useState(false);

  const { mutate: createDeploymentRule, isPending: isCreatingDeploymentRule } = useMutation({
    mutationFn: async (deploymentRule: z.infer<typeof deploymentFormSchema>) => {
      deploymentRule.name = deploymentRule.name || 'Untitled';

      const response = await ApiService.CreateDeploymentRule({
        ...deploymentRule,
        conditions: deploymentRule.conditions.map(({ value, operator, ...condition }) => {
          if (operator === DeploymentRuleOperator.In || operator === DeploymentRuleOperator.Nin) {
            return {
              ...condition,
              operator,
              value: Array.isArray(value) ? value.map((v: { value: string }) => v.value) : [],
            };
          } else {
            return {
              ...condition,
              operator,
              value: (value as { value: string }).value,
            };
          }
        }),
        formIds: [...deploymentRule.inspectionFormIds, ...deploymentRule.repairFormIds],
        scheduleIds: deploymentRule.scheduleIds,
      });
      const { deploymentRule: created, recalcRunId } = await response.json();
      return { createdId: created.id, recalcRunId };
    },
    onSuccess: ({ createdId, recalcRunId }, variables) => {
      queryClient.invalidateQueries({ queryKey: deploymentRuleKeys.all });
      toast.success('Deployment rule created successfully');
      navigate(getWorkspaceRoute('/deployments'), {
        state: { showFeedback: true, deploymentRuleId: createdId, deploymentRuleName: variables.name, recalcRunId },
      });
    },
    onError: () => {
      toast.error('Failed to create deployment rule');
    },
  });

  const { mutate: updateDeploymentRule, isPending: isUpdatingDeploymentRule } = useMutation({
    mutationFn: async (values: z.infer<typeof deploymentFormSchema>) => {
      if (!deploymentRule) {
        throw new Error('Deployment rule not found');
      }
      const response = await ApiService.UpdateDeploymentRule(deploymentRule.id, {
        name: values.name,
        conditions: values.conditions.map(({ value, operator, ...condition }) => {
          if (operator === DeploymentRuleOperator.In || operator === DeploymentRuleOperator.Nin) {
            return {
              ...condition,
              operator,
              value: Array.isArray(value) ? value.map((v: { value: string }) => v.value) : [],
            };
          } else {
            return {
              ...condition,
              operator,
              value: (value as { value: string }).value,
            };
          }
        }),
        formIds: [...values.inspectionFormIds, ...values.repairFormIds],
        scheduleIds: values.scheduleIds,
      });
      const { recalcRunId } = await response.json();
      return recalcRunId;
    },
    onSuccess: (recalcRunId, variables) => {
      queryClient.invalidateQueries({ queryKey: deploymentRuleKeys.all });
      queryClient.invalidateQueries({ queryKey: deploymentRuleKeys.detail(deploymentRule?.id) });
      toast.success('Deployment rule updated successfully');
      navigate(getWorkspaceRoute('/deployments'), {
        state: { showFeedback: true, deploymentRuleId: deploymentRule?.id, deploymentRuleName: variables.name, recalcRunId },
      });
    },
    onError: () => {
      toast.error('Failed to update deployment rule');
    },
  });

  const { mutate: deleteDeploymentRule, isPending: isDeletingDeploymentRule } = useMutation({
    mutationFn: async (deploymentRuleId: string) => await ApiService.DeleteDeploymentRules({ deploymentRuleIds: [deploymentRuleId] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deploymentRuleKeys.all });
      setOpenDeleteConfirmation(false);
      toast.success('Deployment rule deleted successfully');
      navigate(getWorkspaceRoute('/deployments'));
    },
    onError: () => {
      toast.error('Failed to delete deployment rule');
    },
  });

  const form = useForm<z.infer<typeof deploymentFormSchema>>({
    resolver: zodResolver(deploymentFormSchema),
    defaultValues: deploymentRule
      ? {
          name: deploymentRule.name,
          conditions: deploymentRule.conditions,
          inspectionFormIds: deploymentRule.forms?.filter(({ type }) => type === FormType.InspectionForm).map(({ id }) => id) || [],
          repairFormIds: deploymentRule.forms?.filter(({ type }) => type === FormType.RepairForm).map(({ id }) => id) || [],
          scheduleIds: deploymentRule.schedules?.map(({ id }) => id) || [],
        }
      : {
          name: '',
          conditions: [
            {
              attribute: 'assetType',
              operator: DeploymentRuleOperator.In,
              value: [],
            },
          ],
          inspectionFormIds: [],
          repairFormIds: [],
          scheduleIds: [],
        },
  });
  const {
    formState: { errors, isDirty },
    watch,
    clearErrors,
    setValue,
  } = form;

  const conditionsFieldArray = useFieldArray({
    control: form.control,
    name: 'conditions',
  });

  const name = watch('name');
  const conditions = watch('conditions');
  const activeAttributes = conditions.map(({ attribute }) => attribute);
  const filteredAttributes = useMemo(() => ATTRIBUTE_OPTIONS.filter(({ value }) => !activeAttributes.includes(value)), [activeAttributes]);

  const attributeOptionsMap: Record<
    z.infer<typeof DeploymentRuleConditionSchema>['attribute'],
    { initialOptions?: { label: string; value: string }[]; fetchOptions?: (inputValue: string) => Promise<{ label: string; value: string }[]> }
  > = useMemo(
    () => ({
      assetId: {
        fetchOptions: async (inputValue?: string) => {
          const response = await ApiService.GetAssets({
            limit: 100,
            filters: {
              search: inputValue,
              state: RecordState.Active,
            },
          });
          return (await response.json()).data.map(({ id, internalId }) => ({ label: internalId, value: id }));
        },
      },
      assetType: {
        initialOptions: [
          { label: 'Assets', value: GearbeltAssetType.Assets },
          { label: 'Tools and Equipment', value: GearbeltAssetType.ToolsAndEquipment },
        ],
      },
      assetTagId: {
        fetchOptions: async (inputValue: string) => {
          const response = await ApiService.GetTags({
            limit: 100,
            filters: {
              fields: { entity: TagEntity.Asset },
              search: inputValue,
            },
          });
          return (await response.json()).data.map(({ id, tag }) => ({ label: tag, value: id }));
        },
      },
      assetCategoryId: {
        fetchOptions: async (inputValue: string) => {
          const response = await ApiService.GetAssetCategories({
            limit: 100,
            filters: {
              search: inputValue,
            },
          });
          return (await response.json()).data.map(({ id, name }) => ({ label: name, value: id }));
        },
      },
      assetManufacturer: {
        fetchOptions: async (inputValue: string) => {
          const response = await ApiService.GetManufacturers({
            limit: 100,
            filters: {
              search: inputValue,
            },
          });
          return (await response.json()).data.map(({ make }) => ({ label: make, value: make }));
        },
      },
      assetModel: {},
      assetLocationId: {
        fetchOptions: async (inputValue: string) => {
          const response = await ApiService.GetLocations({
            limit: 100,
            filters: {
              search: inputValue,
              state: RecordState.Active,
            },
          });
          return (await response.json()).data.map(({ id, name }) => ({ label: name, value: id }));
        },
      },
    }),
    [],
  );

  function handleSubmission(data: z.infer<typeof deploymentFormSchema>) {
    if (deploymentRule) {
      updateDeploymentRule(data);
    } else {
      createDeploymentRule(data);
    }
  }

  function handleDeleteDeploymentRule() {
    if (!deploymentRule) {
      throw new Error('Deployment rule not found');
    }
    deleteDeploymentRule(deploymentRule.id);
  }

  function handleSave() {
    form.handleSubmit(handleSubmission)();
    setOpenSaveChanges(false);
  }

  function handleDiscard() {
    navigate(-1);
  }

  function handleClose() {
    if (isDirty) {
      setOpenSaveChanges(true);
    } else {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/dashboard');
      }
    }
  }

  useLayoutEffect(() => {
    useFormTopbarStore.getState().setInfo(
      <div className="flex h-[55px] w-full items-center gap-[7px]">
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <div>
                <DeploymentIcon className="bg-grey-2 rounded-md p-1" size={40} />
              </div>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent side="bottom" sideOffset={0} className="bg-default-text pointer-events-auto text-xs font-medium text-white">
                <TooltipArrow className="fill-default-text" />
                Deployment Rule
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        </TooltipProvider>
        <div className="mt-[1px] flex flex-col gap-[1px]">
          <div className="flex items-center gap-1">
            {isLoading ? (
              <div className="bg-grey-2 my-[2px] h-[24px] w-52 animate-pulse rounded" />
            ) : (
              <AutoResizeInput
                value={name}
                onChange={(e) => {
                  setValue('name', e.target.value, { shouldDirty: true });
                }}
                placeholder="Untitled"
                className="hover:ring-grey-3 focus-visible:ring-default-blue h-auto max-w-[calc(70vw)] border-none bg-transparent px-1 py-0 text-xl font-medium hover:ring-2 focus-visible:ring-2"
              />
            )}
            {!isLoading && deploymentRule && (
              <div className="text-secondary-text mt-[1px] ml-[1px] text-xs">{EntityRefUtil.createDeploymentRuleRef(deploymentRule.number)}</div>
            )}
          </div>
          <div className="flex h-[25px] items-end gap-2">
            <Button type="button" className={`pointer-events-none ml-1 h-[25px] rounded-none rounded-t-md px-[5px] py-2 text-sm`}>
              Edit
            </Button>
          </div>
        </div>
      </div>,
    );

    useFormTopbarStore.getState().setActions(
      <div className="flex items-center gap-1">
        {deploymentRule && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" aria-label="More actions" className="mr-[1px]">
                <EllipsisHorizontalIcon />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setOpenDeleteConfirmation(true)} className="text-default-red focus:bg-grey-1 focus:text-default-red">
                <DeleteIcon className="mr-1" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <Button className="mx-[1px] font-medium" type="submit" variant="success" disabled={!isDirty || isCreatingDeploymentRule || isUpdatingDeploymentRule}>
          Save
        </Button>
        <Button type="button" variant="ghost" aria-label="Close" className="mr-[1px]" onClick={handleClose}>
          <CloseIcon />
        </Button>
      </div>,
    );

    return () => {
      useFormTopbarStore.getState().setActions(<></>);
    };
  }, [isDirty, navigate, name, deploymentRule, isLoading, isCreatingDeploymentRule, isUpdatingDeploymentRule]);

  useEffect(() => {
    if (deploymentRule && !isLoading) {
      form.reset({
        name: deploymentRule.name,
        conditions: deploymentRule.conditions,
        inspectionFormIds: deploymentRule.forms?.filter(({ type }) => type === FormType.InspectionForm).map(({ id }) => id) || [],
        repairFormIds: deploymentRule.forms?.filter(({ type }) => type === FormType.RepairForm).map(({ id }) => id) || [],
        scheduleIds: deploymentRule.schedules?.map(({ id }) => id) || [],
      });
    }
  }, [deploymentRule, isLoading, form]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [isDirty, navigate]);

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmission)} className="fixed inset-0 z-30 flex h-screen w-full flex-col bg-white">
        <FormTopbar />
        <div className="bg-grey-1 flex min-h-0 w-full flex-1 items-start justify-center gap-2 overflow-y-auto py-6">
          <div className="rounded-10 flex min-h-full w-[680px] flex-col gap-4 bg-white p-6 min-[1440px]:w-[840px]">
            {isLoading ? (
              <div className="animate-pulse space-y-2">
                <div className="bg-grey-2 h-6 w-1/4 rounded" />
                <div className="bg-grey-2 h-4 w-3/4 rounded" />

                <div className="bg-grey-2 h-32 w-full rounded" />
                <div className="bg-grey-2 h-48 w-full rounded" />
              </div>
            ) : (
              <>
                <div className="flex h-[56px] flex-col justify-between">
                  <div className="flex items-center justify-between gap-2">
                    <h2 className="text-xl font-medium">Rule Settings</h2>
                    <div className="flex items-center gap-1">
                      <Link
                        to="https://help.sitemate.com/en/articles/11063127-how-to-create-deployment-rules-in-gearbelt"
                        target="_blank"
                        className="bg-grey-1 hover:bg-grey-2 inline-flex w-auto gap-1 rounded p-1 text-sm font-medium">
                        <QuestionIcon className="h-4 w-4" />
                        Learn more
                      </Link>
                    </div>
                  </div>
                  <div className="text-sm">Deployment rules allow you to deploy forms to assets based on defined conditions.</div>
                </div>

                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <Label>
                      <b>IF</b> assets match these conditions:
                    </Label>
                    <div className="flex flex-col gap-2">
                      {conditionsFieldArray.fields.map((field, index) => (
                        <Fragment key={field.id}>
                          <div className="flex items-start gap-1">
                            <Controller
                              control={form.control}
                              name={`conditions.${index}.attribute`}
                              render={({ field: { value: attributeValue, onChange } }) => (
                                <div className="flex flex-1 items-start gap-1">
                                  <Select<(typeof ATTRIBUTE_OPTIONS)[number]>
                                    placeholder="Select..."
                                    className="min-w-[200px]"
                                    options={filteredAttributes}
                                    value={{
                                      label: (ATTRIBUTE_OPTIONS.find(({ value: attributeOptionValue }) => attributeOptionValue === attributeValue)?.label ||
                                        '') as (typeof ATTRIBUTE_OPTIONS)[number]['label'],
                                      value: attributeValue,
                                    }}
                                    onChange={(option) => {
                                      onChange(option?.value);
                                      form.setValue(`conditions.${index}.value`, []);
                                    }}
                                  />

                                  <Controller
                                    control={form.control}
                                    name={`conditions.${index}.operator`}
                                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                                      <Select<{ label: string; value: z.infer<typeof DeploymentRuleConditionSchema>['operator'] }>
                                        placeholder="Select..."
                                        className="min-w-[85px]"
                                        options={OPERATOR_OPTIONS}
                                        error={!!error}
                                        value={{
                                          label: OPERATOR_OPTIONS.find(({ value: operatorOptionValue }) => operatorOptionValue === value)?.label || '',
                                          value,
                                        }}
                                        onChange={(option) => onChange(option?.value)}
                                      />
                                    )}
                                  />

                                  {(() =>
                                    match(ATTRIBUTE_FORM_INPUT_TYPE_MAP[attributeValue as keyof typeof ATTRIBUTE_FORM_INPUT_TYPE_MAP])
                                      .with('multi-select', () => (
                                        <Controller
                                          control={form.control}
                                          name={`conditions.${index}.value`}
                                          render={({ field: { value, onChange }, fieldState: { error } }) => (
                                            <AsyncMultiSelect<{ label: string; value: string }>
                                              key={`${index}-${attributeValue}`}
                                              placeholder="Select..."
                                              className="w-full"
                                              defaultOptions={attributeOptionsMap[attributeValue as keyof typeof attributeOptionsMap].initialOptions || true}
                                              loadOptions={attributeOptionsMap[attributeValue].fetchOptions ?? undefined}
                                              value={value}
                                              error={!!error}
                                              onChange={(values) => onChange(values || [])}
                                              closeMenuOnSelect={false}
                                            />
                                          )}
                                        />
                                      ))
                                      .with('creatable-multi-select', () => (
                                        <Controller
                                          control={form.control}
                                          name={`conditions.${index}.value`}
                                          render={({ field: { onChange, value }, fieldState: { error } }) => (
                                            <AsyncCreatableMultiSelect<{ label: string; value: string }>
                                              key={`${index}-${attributeValue}`}
                                              placeholder="Select..."
                                              className="w-full"
                                              defaultOptions={attributeOptionsMap[attributeValue as keyof typeof attributeOptionsMap].initialOptions || true}
                                              loadOptions={attributeOptionsMap[attributeValue].fetchOptions ?? undefined}
                                              value={value}
                                              error={!!error}
                                              onChange={(values) => onChange(values || [])}
                                              isClearable
                                              closeMenuOnSelect={false}
                                            />
                                          )}
                                        />
                                      ))
                                      .with('text', () => <Input placeholder="Enter..." />)
                                      .exhaustive())()}
                                </div>
                              )}
                            />
                            {conditionsFieldArray.fields.length > 1 && (
                              <Button
                                variant="ghost"
                                icon={<CloseIcon />}
                                className="text-default-red w-fit"
                                type="button"
                                onClick={() => conditionsFieldArray.remove(index)}
                              />
                            )}
                          </div>

                          {conditionsFieldArray.fields.length > index + 1 && <span className="text-secondary-text -my-1 text-xs font-medium">AND</span>}
                        </Fragment>
                      ))}
                    </div>
                    {conditions.length < 3 && (
                      <Button
                        variant="default"
                        className="w-fit"
                        onClick={() =>
                          conditionsFieldArray.append({
                            attribute: filteredAttributes[0].value,
                            operator: DeploymentRuleOperator.In,
                            value: [],
                          })
                        }
                        icon={<PlusIcon />}
                        type="button">
                        Add Condition
                      </Button>
                    )}

                    {errors.conditions && Array.isArray(errors.conditions) && errors.conditions.some((condition) => condition?.value) && (
                      <p className="text-default-red text-sm">
                        {errors.conditions?.[0]?.value?.message ? 'At least one condition is required' : 'Value is required'}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label>
                      <b>THEN</b> Deploy these forms to those assets:
                    </Label>

                    <div className="flex items-center gap-2">
                      <div className="flex w-[227px] items-center gap-1">
                        <FormChecklistIcon />
                        <span className="text-sm">Inspection Forms</span>
                      </div>

                      <Controller
                        control={form.control}
                        name="inspectionFormIds"
                        render={({ field: { onChange } }) => (
                          <AsyncMultiSelect<{ label: string; value: string }>
                            placeholder="Select one or multiple..."
                            className="flex-1"
                            defaultOptions
                            loadOptions={async (inputValue: string) => {
                              const response = await ApiService.GetForms({
                                limit: 100,
                                filters: {
                                  search: inputValue,
                                  state: RecordState.Active,
                                  fields: {
                                    type: [FormType.InspectionForm],
                                  },
                                },
                              });
                              return (await response.json()).data.map(({ id, name }) => ({ label: name, value: id }));
                            }}
                            onChange={(option) => {
                              if (option?.length) {
                                clearErrors('inspectionFormIds');
                              }
                              onChange(option?.map(({ value }) => value) || []);
                            }}
                            closeMenuOnSelect={false}
                            defaultValue={
                              deploymentRule?.forms
                                ?.filter(({ type }) => type === FormType.InspectionForm)
                                .map(({ id, name }) => ({ label: name, value: id })) || []
                            }
                          />
                        )}
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex w-[227px] items-center gap-1">
                        <WarningTriangleIcon />
                        <span className="text-sm">Repair Forms</span>
                      </div>

                      <Controller
                        control={form.control}
                        name="repairFormIds"
                        render={({ field: { onChange } }) => (
                          <AsyncMultiSelect<{ label: string; value: string }>
                            placeholder="Select one..."
                            className="flex-1"
                            defaultOptions
                            loadOptions={async (inputValue: string) => {
                              const response = await ApiService.GetForms({
                                limit: 100,
                                filters: {
                                  search: inputValue,
                                  state: RecordState.Active,
                                  fields: {
                                    type: [FormType.RepairForm],
                                  },
                                },
                              });
                              return (await response.json()).data.map(({ id, name }) => ({ label: name, value: id }));
                            }}
                            onChange={(option) => {
                              if (option?.length) {
                                clearErrors('repairFormIds');
                              }
                              onChange(option?.map(({ value }) => value) || []);
                            }}
                            styles={{
                              multiValue: (state) => ({
                                ...state,
                                backgroundColor: 'rgb(var(--default-light-orange))',
                              }),
                              multiValueLabel: (state) => ({
                                ...state,
                                color: 'rgb(var(--default-dark-orange))',
                              }),
                              multiValueRemove: (state) => ({
                                ...state,
                                color: 'rgb(var(--default-dark-orange))',
                              }),
                            }}
                            isClearable
                            defaultValue={
                              deploymentRule?.forms?.filter(({ type }) => type === FormType.RepairForm).map(({ id, name }) => ({ label: name, value: id })) ||
                              []
                            }
                          />
                        )}
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex w-[227px] items-center gap-1">
                        <ClockIcon />
                        <span className="text-sm">Schedules</span>
                      </div>

                      <Controller
                        control={form.control}
                        name="scheduleIds"
                        render={({ field: { onChange } }) => (
                          <AsyncMultiSelect<{ label: string; value: string } | undefined>
                            key={deploymentRule?.schedules
                              ?.filter(({ state }) => state === RecordState.Active)
                              .map(({ id }) => id)
                              .join(',')}
                            placeholder="Select one or multiple..."
                            className="flex-1"
                            defaultOptions
                            loadOptions={async (inputValue: string) => {
                              const response = await ApiService.GetSchedules({
                                limit: 100,
                                filters: {
                                  state: RecordState.Active,
                                  search: inputValue,
                                },
                              });
                              return (await response.json()).data.map(({ id, name }) => ({ label: name, value: id }));
                            }}
                            onChange={(option) => {
                              if (option?.length) {
                                clearErrors('scheduleIds');
                              }
                              onChange(option?.map((option) => option?.value) || []);
                            }}
                            closeMenuOnSelect={false}
                            styles={{
                              multiValue: (state) => ({
                                ...state,
                                backgroundColor: 'rgb(var(--default-light-violet))',
                              }),
                              multiValueLabel: (state) => ({
                                ...state,
                                color: 'rgb(var(--default-dark-violet))',
                              }),
                              multiValueRemove: (state) => ({
                                ...state,
                                color: 'rgb(var(--default-dark-violet))',
                              }),
                            }}
                            isClearable
                            defaultValue={
                              deploymentRule?.schedules
                                ?.filter(({ state }) => state === RecordState.Active)
                                .map(({ id, name }) => ({ label: name, value: id })) || []
                            }
                          />
                        )}
                      />
                    </div>

                    {errors.inspectionFormIds && <p className="text-default-red text-sm">{errors.inspectionFormIds.message}</p>}
                  </div>

                  <DeleteConfirmationDialog
                    open={openDeleteConfirmation}
                    onOpenChange={setOpenDeleteConfirmation}
                    callback={handleDeleteDeploymentRule}
                    loading={isDeletingDeploymentRule || isUpdatingDeploymentRule || isCreatingDeploymentRule}
                    title="Delete deployment rule?"
                    description="Are you sure you want to delete this deployment rule? This action is permanent and cannot be undone."
                  />
                  <SaveChangesDialog
                    open={openSaveChanges}
                    onOpenChange={setOpenSaveChanges}
                    saveCallback={handleSave}
                    discardCallback={handleDiscard}
                    loading={isCreatingDeploymentRule || isUpdatingDeploymentRule || isDeletingDeploymentRule}
                    title="Do you want to save this rule?"
                    description="Save your rule settings before leaving."
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
